<template>
  <TText size="lg" tag="h1" variant="semibold" classes="mb-4">{{
    t('auth.registrationTraveler.google.title')
  }}</TText>
  <div v-if="currentState === 'ERROR'" class="my-4">
    <ApiResponseError :api-response-error="apiResponseError" />
  </div>
  <UForm :schema="schema" :state="state" class="space-y-3" @submit="onSubmit">
    <UFormField required name="emailAddress">
      <UInput
        v-model="state.emailAddress"
        size="lg"
        :placeholder="t('auth.register.email')"
        :aria-label="t('auth.register.email')"
        :title="t('auth.register.email-title')"
        autocomplete="on"
        disabled
      />
    </UFormField>
    <UFormField required name="firstName">
      <UInput
        v-model="state.firstName"
        size="lg"
        :placeholder="t('auth.register.name')"
        :aria-label="t('auth.register.name')"
        :title="t('auth.register.name-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField required name="lastName">
      <UInput
        v-model="state.lastName"
        size="lg"
        :placeholder="t('auth.register.surnames')"
        :aria-label="t('auth.register.surnames')"
        :title="t('auth.register.surnames-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField required name="phone">
      <UInput
        v-model="state.phone"
        size="lg"
        type="tel"
        :placeholder="t('auth.register.phone')"
        :aria-label="t('auth.register.phone')"
        :title="t('auth.register.phone-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField v-if="provinces && provinces.length > 0" name="provinceId">
      <UInputMenu
        v-model="state.provinceId"
        :items="provinces"
        size="lg"
        :placeholder="`${t('auth.register.province') + ' ' + t('form.optional')}`"
        :aria-label="t('auth.register.province')"
        :title="t('auth.register.province-title')"
        label-key="name"
        value-key="value"
      />
    </UFormField>
    <UFormField name="allowComms">
      <UCheckbox
        v-model="state.allowComms"
        class="mt-5"
        :label="t('auth.register.allowComms')"
      />
    </UFormField>
    <UFormField name="privacyPolicy">
      <UCheckbox v-model="state.privacyPolicy" required>
        <template #label>
          <i18n-t keypath="auth.confirmation.label" tag="span" scope="global">
            <template #useConditions>
              <a
                class="underline"
                target="_blank"
                :href="t('auth.confirmation.useConditionsUrl')"
                >{{ t('auth.confirmation.useConditions') }}</a
              >
            </template>
            <template #privacyPolicy>
              <a
                class="underline"
                target="_blank"
                :href="t('auth.confirmation.privacyPolicyUrl')"
                >{{ t('auth.confirmation.privacyPolicy') }}</a
              >
            </template>
          </i18n-t>
        </template>
      </UCheckbox>
    </UFormField>
    <UFormField name="recaptcha">
      <TRecaptcha
        ref="vueRecaptchaRef"
        v-model="state.recaptcha"
        class="mt-6 mb-3"
      />
    </UFormField>

    <div class="flex flex-col items-center">
      <UButton
        class="mt-2 mb-6"
        variant="solid"
        size="xl"
        type="submit"
        :disabled="!isValid"
        :loading="currentState === 'SENDING'"
      >
        {{ t('auth.register.submit') }}
      </UButton>
    </div>
  </UForm>
</template>

<script setup lang="ts">
import type { FormSubmitEvent } from '#ui/types'
import { z } from 'zod'
import { browserFetch as fetchSignUp } from '~~/modules/api/rurality/v3/users/sign-up'
import { browserFetch as fetchLogin } from '~~/modules/api/rurality/v3/users/login-google'
import { paramsSchema } from '~~/shared/api/rurality/v3/users/sign-up/schema'
import { useZodValidator } from '~~/modules/form/composables/useZodValidator'
import { scrollToTop } from '~/composables/useAnimations'
import { browserFetch as getProvinces } from '~~/modules/api/rurality/v3/provinces/detail'
import { useToProvinces } from '~/composables/transformers/useToProvinces'
import { signUpOrigin } from '~~/modules/auth/constants'
import type { UserInfo } from '~~/modules/auth/entities/UserInfo'
import loggedUserRedirect from '~~/modules/auth/composables/useAuthRedirect'

const props = defineProps<{
  userInfo: UserInfo
  isModal?: boolean
}>()

const emit = defineEmits(['login-success'])

interface ChildComponent {
  triggerReset(): void
}

const apiResponseError: Ref<ApiResponseError | null> = ref(null)
const vueRecaptchaRef = ref<ChildComponent | null>(null)

const provincesResponse = await getProvinces()
const provincesRaw = provincesResponse.success
  ? provincesResponse.success.data.provinces
  : []

const provinces = useToProvinces().fromRegister(provincesRaw)

const recaptchaReset = () => {
  state.recaptcha = undefined
  vueRecaptchaRef.value?.triggerReset()
}

const { validateEmail, validatePhone, validateRequiredCheckbox } =
  useZodValidator()

const schema = paramsSchema.extend({
  firstName: z.string().min(2).max(30),
  lastName: z.string().min(2).max(30),
  phone: validatePhone()
    .transform(value => (value === '' ? null : value))
    .transform(value => (value === undefined ? null : value)),
  provinceId: z
    .number()
    .int()
    .optional()
    .nullable()
    .transform(value => (value === undefined ? null : value)),
  emailAddress: validateEmail(),
  emailAddressRepeat: validateEmail(),
  privacyPolicy: validateRequiredCheckbox(),
  googleId: z.string(),
})

const state = reactive({
  firstName: props.userInfo.firstName,
  lastName: props.userInfo.lastName,
  phone: undefined,
  emailAddress: props.userInfo.emailAddress,
  emailAddressRepeat: props.userInfo.emailAddress,
  provinceId: undefined,
  password: null,
  allowComms: false,
  privacyPolicy: undefined,
  recaptcha: undefined,
  facebookId: null,
  googleId: props.userInfo.id,
  appleId: null,
  userType: 'TRAVELER' as const,
  signUpOrigin: signUpOrigin.PAGINA_REGISTRO_GOOGLE,
})

const _states = {
  IDLE: 'IDLE',
  INVALID: 'INVALID',
  VALID: 'VALID',
  SENDING: 'SENDING',
  ERROR: 'ERROR',
}

const currentState: Ref<keyof typeof _states> = ref('IDLE')

const isValid = computed(() => {
  return schema.safeParse(state).success && state.privacyPolicy === true
})

if (props.userInfo) {
  const { firstName, lastName, emailAddress, id } = props.userInfo

  state.firstName = firstName
  state.lastName = lastName
  state.emailAddress = emailAddress
  state.emailAddressRepeat = emailAddress
  state.googleId = id
}

async function onSubmit(event: FormSubmitEvent<Record<string, unknown>>) {
  const result = schema.safeParse(event.data)

  if (result.success) {
    currentState.value = 'SENDING'

    const parsedData = result.data

    const signUp = await fetchSignUp(parsedData)

    if (signUp.success && signUp.success.data) {
      const login = await fetchLogin({
        emailAddress: parsedData.emailAddress,
        googleId: parsedData.googleId,
      })
      if (login.success && login.success.data) {
        if (props.isModal) {
          emit('login-success')
        } else {
          navigateTo(
            loggedUserRedirect({
              userType: login.success.data.userType,
              userId: login.success.data.userId,
            }),
            {
              external: true,
            },
          )
        }
      } else {
        apiResponseError.value = login.error
        currentState.value = 'ERROR'
        scrollToTop()
        recaptchaReset()
      }
    } else {
      apiResponseError.value = signUp.error
      currentState.value = 'ERROR'
      scrollToTop()
      recaptchaReset()
    }
  }
}
</script>
